import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import BlogArticleLayout from '@/components/base/BlogArticleLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Blog', to: '/blog/' },
  { label: 'Baie vitrée qui déraille ou sort du rail' },
];

const jsonLdBlogPosting = {
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: 'Baie vitrée qui déraille ou sort de son rail dans l\'Hérault',
  description: 'Votre baie vitrée déraille ou sort du rail ? Causes (rail usé, roulettes HS), comment la remettre en sécurité, et importance de l\'entretien du rail, surtout en zone littorale dans l\'Hérault.',
  url: 'https://heraultroulettesbaiesvitrees.fr/blog/baie-vitree-deraille-sort-du-rail/',
  datePublished: '2026-06-05',
  dateModified: '2026-07-26',
  author: { '@type': 'Organization', name: 'DFPRO Hérault' },
  publisher: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Que faire immédiatement si ma baie coulissante sort de son rail ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Ne tentez pas de la remettre en place seul. Une baie vitrée pèse très lourd et peut basculer. Sécurisez la zone, éloignez les enfants et les animaux, et bloquez le vantail avec un objet lourd (meuble, cale) pour éviter qu\'il ne tombe. Appelez un professionnel sans tarder.',
      },
    },
    {
      '@type': 'Question',
      name: 'Pourquoi une baie coulissante déraille-t-elle ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Les causes principales sont : un rail usé ou écrasé qui ne guide plus correctement les roulettes, des galets complètement détruits qui sortent de leur logement, un vantail déséquilibré après un choc, ou une butée de fin de course manquante qui permet au vantail de dépasser le rail.',
      },
    },
    {
      '@type': 'Question',
      name: 'Peut-on réparer un rail de baie coulissante abîmé ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Un rail légèrement marqué peut être nettoyé et poncé pour retrouver une surface lisse. Un rail déformé ou écrasé doit généralement être remplacé, car la déformation empêche les roulettes de rouler correctement et provoque des déraillements à répétition.',
      },
    },
  ],
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/', name: 'Accueil' } },
    { '@type': 'ListItem', position: 2, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/', name: 'Blog' } },
    { '@type': 'ListItem', position: 3, item: { '@id': 'https://heraultroulettesbaiesvitrees.fr/blog/baie-vitree-deraille-sort-du-rail/', name: 'Baie vitrée qui déraille' } },
  ],
};

export default function ArticleBaieVitreeDeraille() {
  return (
    <>
      <SEO
        heroImage="/images/blog-baie-deraille.jpg"
        title="Baie vitrée qui déraille ou sort du rail : solutions"
        description="Baie vitrée qui sort du rail dans l'Hérault ? Causes, sécurisation d'urgence et solutions. Entretien du rail en zone littorale. Intervention 7j/7. DFPRO."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/blog/baie-vitree-deraille-sort-du-rail/"
      />
      <BlogArticleLayout
        title="Baie vitrée qui déraille ou sort de son rail dans l&apos;Hérault"
        breadcrumb={breadcrumb}
        jsonLdBlogPosting={jsonLdBlogPosting}
        jsonLdFAQ={jsonLdFAQ}
        jsonLdBreadcrumb={jsonLdBreadcrumb}
        datePublished="5 juin 2026"
        dateModified="26 juillet 2026"
      >
        <p>
          Le bruit est souvent le premier signal : un claquement métallique sec, puis soudainement la baie vitrée qui bascule légèrement, un vantail qui n&apos;est plus dans son axe. Une baie coulissante qui déraille ou sort de son rail est une situation impressionnante — et potentiellement dangereuse. Dans l&apos;Hérault, nous intervenons régulièrement sur ce type d&apos;urgence, des quartiers résidentiels de Montpellier jusqu&apos;aux résidences de bord de mer. Voici ce qu&apos;il faut savoir et comment réagir.
        </p>

        <h2>Premiers réflexes : ne surtout pas forcer</h2>
        <p>
          Le premier réflexe, compréhensible, est de vouloir remettre le vantail en place immédiatement. C&apos;est une erreur qui peut avoir des conséquences graves. Un vantail de baie coulissante pèse entre 80 et 150 kg, parfois davantage pour les grands formats. S&apos;il est sorti de son rail, il n&apos;est plus guidé et peut basculer brusquement. Sécurisez d&apos;abord la zone : éloignez les enfants et les animaux de compagnie, calez le vantail avec un objet lourd pour éviter qu&apos;il ne tombe, et appelez un professionnel sans tarder. À Montpellier et dans sa métropole, nos équipes DFPRO Hérault interviennent 7 jours sur 7, y compris le week-end.
        </p>

        <h2>Pourquoi une baie coulissante sort-elle de son rail ?</h2>
        <p>
          Les causes de déraillement sont rarement mystérieuses, mais il est important de les comprendre pour éviter une récidive. La plus fréquente est l&apos;usure combinée des roulettes et du rail : quand les galets sont détruits, le vantail repose directement sur le profilé aluminium, ce qui creuse et écrase progressivement la surface de roulement. À un moment donné, la gorge du rail n&apos;est plus assez profonde pour retenir les roulettes, et le vantail sort — généralement du côté intérieur.
        </p>
        <p>
          Une autre cause est la butée de fin de course manquante ou cassée. Ces petites pièces, souvent en caoutchouc ou en nylon, empêchent le vantail d&apos;aller trop loin. Avec le temps, elles se détériorent ou se détachent, et un vantail ouvert trop vigoureusement peut dépasser le rail et sortir. Enfin, un choc — un meuble lourd heurté contre la baie, une rafale de vent qui claque un vantail — peut suffire à provoquer un déraillement, surtout si les roulettes étaient déjà en mauvais état.
        </p>

        {/* CTA milieu */}
        <div className="my-8 p-5 md:p-6 bg-background-100 rounded-lg border border-accent-500/20 text-center">
          <p className="font-heading font-bold text-primary-500 text-base mb-2">
            Votre baie vitrée est sortie de son rail ? Urgence dans l&apos;Hérault ?
          </p>
          <p className="text-sm text-foreground-500 font-body mb-4">
            Nos techniciens interviennent 7j/7 pour sécuriser votre baie coulissante et <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparer votre rail</Link>.
          </p>
          <a href="tel:+33980802242" className="btn-call text-sm px-6 py-3 whitespace-nowrap inline-flex">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm"></i>
            </span>
            09 80 80 22 42
          </a>
        </div>

        <h2>Rail marqué ou écrasé : peut-on le réparer ?</h2>
        <p>
          Tout dépend du degré de dommage. Un rail simplement marqué — avec des traces de frottement superficielles — peut être nettoyé et légèrement poncé pour retrouver une surface lisse. En revanche, un rail écrasé, c&apos;est-à-dire dont le profilé aluminium a été déformé en profondeur par le frottement du vantail, ne peut pas être réparé de façon satisfaisante. La déformation, même minime, crée un point dur sur lequel les roulettes butent, et le risque de nouveau déraillement est élevé.
        </p>
        <p>
          Dans la majorité des cas, nous recommandons le <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement du rail de baie coulissante</Link>. L&apos;opération consiste à extraire l&apos;ancien rail, nettoyer le seuil, et poser un rail neuf parfaitement compatible avec le système de roulettes de la baie. C&apos;est une intervention technique mais maîtrisée, que nos équipes réalisent en quelques heures, sans nécessiter le remplacement de la menuiserie complète.
        </p>

        <h2>Remettre le vantail sur le rail : une opération à deux</h2>
        <p>
          Une fois le rail remis en état ou remplacé, nos techniciens procèdent à la repose du vantail. Cette opération requiert deux personnes, des ventouses de levage et une technique précise pour engager les roulettes dans le rail sans forcer. Le vantail est d&apos;abord présenté incliné, les roulettes sont alignées sur la gorge du rail, puis le vantail est redressé progressivement jusqu&apos;à la verticale. Une fois en place, les butées de fin de course sont vérifiées — et remplacées si nécessaire — et un réglage complet du coulissement et de la compression des joints est effectué.
        </p>

        <h2>L&apos;entretien du rail en zone littorale</h2>
        <p>
          Le littoral héraultais — Sète, Frontignan, Agde, La Grande-Motte, Marseillan — est un environnement particulièrement agressif pour les rails de baie coulissante. L&apos;air chargé en embruns salins se dépose sur le rail, et combiné à l&apos;humidité, accélère la corrosion de l&apos;aluminium et l&apos;oxydation des composants métalliques des roulettes. Le sable fin, soulevé par le vent, s&apos;accumule dans le rail et agit comme un abrasif.
        </p>
        <p>
          L&apos;entretien préventif est simple mais doit être régulier : un nettoyage du rail tous les deux à trois mois à l&apos;aspirateur, suivi d&apos;un passage au chiffon humide à l&apos;eau claire. Évitez absolument les produits ménagers agressifs, les détergents ou les lubrifiants en aérosol, qui attirent la poussière et encrassent davantage le mécanisme. Ce petit geste d&apos;entretien peut prolonger considérablement la durée de vie de votre baie coulissante et prévenir les déraillements coûteux.
        </p>

        <h2>Questions fréquentes</h2>

        <h3>Une baie qui a déraillé une fois va-t-elle dérailler à nouveau ?</h3>
        <p>
          Si la cause du déraillement n&apos;est pas traitée — par exemple si les roulettes usées ne sont pas changées — le risque de récidive est très élevé. En revanche, après une réparation complète incluant le remplacement des galets et la remise en état du rail, une baie correctement réglée ne déraillera pas plus qu&apos;une baie neuve. L&apos;entretien régulier fait le reste.
        </p>

        <h3>Combien de temps faut-il pour réparer une baie qui a déraillé ?</h3>
        <p>
          Cela dépend de l&apos;état du rail et des roulettes. Si le rail est intact et que seules les roulettes sont à changer, l&apos;intervention prend environ 1h30 à 2h. Si le rail doit être remplacé, comptez une demi-journée. Dans tous les cas, nos techniciens vous donnent une estimation précise après diagnostic sur place.
        </p>
      </BlogArticleLayout>
    </>
  );
}